import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MarkdownLinkIcon, MarkdownUnlinkIcon, LinkExternalAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { flip, offset } from '@udecode/plate-floating';
import { encodeUrlIfNeeded } from '@udecode/plate-link';
import { useLinkToolbarButton, useLinkToolbarButtonState, LinkPlugin, useFloatingLinkEdit, useFloatingLinkEditState, useFloatingLinkInsert, useFloatingLinkInsertState } from '@udecode/plate-link/react';
import { useFormInputProps, useEditorPlugin, useEditorRef, usePluginOption } from '@udecode/plate/react';
import { useEffect, useRef } from 'react';
import { Button } from '../Button';
import { Stack } from '../Layout';
import { Separator } from '../Separator';
import { StyledLinkInput, StyledFloatingToolbar, StyledButton } from './styled';
import { useToolbarButtonClick } from './utils';
const floatingOptions = {
    middleware: [
        offset(12),
        flip({
            fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
            padding: 12
        })
    ],
    placement: 'bottom'
};
export function LinkButton() {
    const state = useLinkToolbarButtonState();
    const { props } = useLinkToolbarButton(state);
    const onClick = useToolbarButtonClick(props.onClick);
    return (_jsx(StyledButton, { variant: 'tertiaryGray', size: 'm', ...props, icon: _jsx(MarkdownLinkIcon, { type: 'extended' }), onClick: onClick, "aria-label": 'Insert link', "data-test-id": 'markdown-editor-link-button' }));
}
function LinkInput() {
    const editor = useEditorRef();
    const insertState = useFloatingLinkInsertState({
        floatingOptions
    });
    const { hidden, props: insertProps, ref: insertRef } = useFloatingLinkInsert(insertState);
    const editState = useFloatingLinkEditState({
        floatingOptions
    });
    const { editButtonProps, props: editProps, ref: editRef, unlinkButtonProps } = useFloatingLinkEdit({ ...editState });
    const inputProps = useFormInputProps({
        preventDefaultOnEnterKeydown: true
    });
    const { getOptions, setOption } = useEditorPlugin(LinkPlugin);
    const inputRef = useRef(null);
    const updated = usePluginOption(LinkPlugin, 'updated');
    useEffect(() => {
        if (inputRef.current && updated) {
            setTimeout(() => {
                const input = inputRef.current;
                if (!input)
                    return;
                const url = getOptions().url;
                input.focus();
                input.value = url ? decodeURI(url) : '';
            }, 0);
        }
    }, [getOptions, updated]);
    const onInputChange = (value) => {
        setOption('url', encodeUrlIfNeeded(value));
    };
    if (hidden)
        return null;
    const input = (_jsx(Stack, { ...inputProps, height: '100%', align: 'center', children: _jsx(StyledLinkInput, { icon: _jsx(MarkdownLinkIcon, { type: 'extended' }), defaultValue: getOptions().url, ref: inputRef, onChange: onInputChange, iconPlacement: 'left', placeholder: 'Link URL', "data-test-id": 'markdown-editor-link-url-input' }) }));
    const linkNode = editor.api.node({
        match: { type: editState.editor.getType(LinkPlugin) }
    })?.[0];
    const editContent = editState.isEditing ? (input) : (_jsxs(Stack, { direction: 'horizontal', gap: theme.spaces.x4, alignY: 'center', children: [_jsx(Button, { variant: 'tertiaryGray', size: 's', ...editButtonProps, "data-test-id": 'markdown-editor-link-button', children: "Edit link" }), _jsx(Separator, { orientation: 'vertical' }), _jsx(Button, { variant: 'tertiaryGray', size: 's', icon: _jsx(LinkExternalAIcon, { type: 'default' }), elementType: 'anchor', "aria-label": 'Open link in a new tab', target: '_blank', href: linkNode?.url, onClick: (e) => {
                    e.stopPropagation();
                }, "data-test-id": 'markdown-editor-open-link-button' }), _jsx(Separator, { orientation: 'vertical' }), _jsx(Button, { variant: 'tertiaryGray', size: 's', icon: _jsx(MarkdownUnlinkIcon, { type: 'extended' }), ...unlinkButtonProps, "aria-label": 'Remove link', "data-test-id": 'markdown-editor-unlink-button' })] }));
    return (_jsxs(_Fragment, { children: [_jsx(StyledFloatingToolbar, { ref: insertRef, ...insertProps, align: 'center', children: input }), _jsx(StyledFloatingToolbar, { ref: editRef, ...editProps, align: 'center', children: editContent })] }));
}
export const CustomLinkPlugin = LinkPlugin.extend({
    render: {
        afterEditable: () => _jsx(LinkInput, {})
    }
});
